import React, { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { useSelector } from "react-redux"
import { toast } from "react-toastify"
import CommonSelect from "../../../../core/common/commonSelect"
import Table from "../../../../core/common/dataTable/index"
import { Option } from "../../../../core/common/multiSelect"
import ImageWithBasePath from "../../../../core/common/imageWithBasePath"
import { getAllClasses, type Class } from "../../../../service/classes"
import { listGroups, type Group } from "../../../../service/group"
import { getEnrollments, Enrollment } from "../../../../service/enrollment"
import { selectCurrentSession } from "../../../../core/data/redux/slice/sessionsSlice"
import { selectSystemsForCurrentBranch } from "../../../../core/data/redux/slice/systemsSlice"
import { all_routes } from "../../../router/all_routes"
import { useFeeStickerPrinting } from "../feeStickerPrinting"

// No pagination on this screen — the whole matching roster loads at once.
const ALL_STUDENTS_LIMIT = 2000

const FeeStickerPrint = () => {
  const routes = all_routes
  const currentSession = useSelector(selectCurrentSession)
  const systems = useSelector(selectSystemsForCurrentBranch)
  const { openFeeStickers, feeStickerLoading, feeStickerModal } = useFeeStickerPrinting()

  // Pending selections shown in the toolbar — System and Class are required,
  // Section and Group are optional. Nothing is fetched until Search is
  // clicked; see `hasSearched` below.
  const [draftSystem, setDraftSystem] = useState<string | undefined>(undefined)
  const [draftClassId, setDraftClassId] = useState("")
  const [draftSectionId, setDraftSectionId] = useState("")
  const [draftGroupId, setDraftGroupId] = useState("")

  const [appliedFilters, setAppliedFilters] = useState({
    system: undefined as string | undefined,
    classId: "",
    sectionId: "",
    groupId: "",
  })

  // No data is shown until the admin explicitly clicks Search.
  const [hasSearched, setHasSearched] = useState(false)

  const [classes, setClasses] = useState<Class[]>([])
  const [classOptions, setClassOptions] = useState<Option[]>([])
  const [loadingClasses, setLoadingClasses] = useState(false)
  const [groups, setGroups] = useState<Group[]>([])
  const [loadingGroups, setLoadingGroups] = useState(false)

  const [students, setStudents] = useState<Enrollment[]>([])
  const [selectedStudents, setSelectedStudents] = useState<Enrollment[]>([])
  const [loading, setLoading] = useState(false)
  const [searchText, setSearchText] = useState("")

  // Lets an admin jump straight to a specific student by GR number (or name)
  // without first picking System + Class — the backend's searchText filter
  // already works unscoped, this just exposes that path directly.
  const [quickSearchValue, setQuickSearchValue] = useState("")

  // Sections available for the currently selected class only — a class's own
  // `sections` array (already scoped to this campus/session by the backend),
  // not the global section catalog.
  const sectionOptions: Option[] = useMemo(() => {
    const selectedClass = classes.find((c) => c._id === draftClassId)
    return (selectedClass?.sections || []).map((section: any) => ({
      label: section.name,
      value: section._id,
    }))
  }, [classes, draftClassId])

  const groupOptions: Option[] = useMemo(
    () => groups.map((g) => ({ label: g.name, value: g._id })),
    [groups]
  )

  const fetchClasses = async (system: string | undefined) => {
    if (!system) {
      setClasses([])
      setClassOptions([])
      return
    }
    try {
      setLoadingClasses(true)
      const response = await getAllClasses({ limit: 100, system, session: currentSession?._id })
      setClasses(response.classes)
      setClassOptions(response.classes.map((cls: Class) => ({ label: cls.name, value: cls._id })))
    } catch (error) {
      console.error("Error fetching classes:", error)
      toast.error("Failed to fetch classes")
    } finally {
      setLoadingClasses(false)
    }
  }

  // Groups (academic streams, e.g. Computer/Biology) that this class belongs
  // to — only shown as a dropdown when a class actually has any.
  const fetchGroups = async (system: string | undefined, classId: string) => {
    if (!classId) {
      setGroups([])
      return
    }
    try {
      setLoadingGroups(true)
      const res = await listGroups({ system, class: classId })
      setGroups(res?.data || [])
    } catch (error) {
      console.error("Error fetching groups:", error)
      setGroups([])
    } finally {
      setLoadingGroups(false)
    }
  }

  useEffect(() => {
    fetchClasses(draftSystem)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftSystem])

  const fetchStudents = async () => {
    try {
      setLoading(true)
      const response = await getEnrollments({
        page: 1,
        limit: ALL_STUDENTS_LIMIT,
        session: currentSession?._id,
        system: appliedFilters.system,
        class: appliedFilters.classId,
        section: appliedFilters.sectionId || undefined,
        group: appliedFilters.groupId || undefined,
        searchText: searchText || undefined,
        // A fee sticker is for a student currently on the rolls — a TC'd or
        // left-without-information student has nothing to stick a fee label
        // onto. Struck-off stays: the account is still open.
        statusScope: "onroll",
      })
      setStudents(response.data || [])
    } catch (error) {
      console.error("Error fetching students:", error)
      toast.error("Failed to fetch students")
      setStudents([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!hasSearched) return
    fetchStudents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSearched, appliedFilters, searchText])

  // System -> Class -> Section/Group(optional), each step resets the ones
  // after it so a stale section/group from a previous class can't linger.
  const handleSystemChange = (systemItem: any) => {
    setDraftSystem(systemItem?._id)
    setDraftClassId("")
    setDraftSectionId("")
    setDraftGroupId("")
    setGroups([])
  }

  const handleClassFilterChange = (selectedOption: any) => {
    const classId = selectedOption?.value || ""
    setDraftClassId(classId)
    setDraftSectionId("")
    setDraftGroupId("")
    fetchGroups(draftSystem, classId)
  }

  const handleSectionFilterChange = (selectedOption: any) => {
    setDraftSectionId(selectedOption?.value || "")
  }

  const handleGroupFilterChange = (selectedOption: any) => {
    setDraftGroupId(selectedOption?.value || "")
  }

  // System and Class are required; Section and Group are optional.
  const canSearch = Boolean(draftSystem && draftClassId)

  const handleSearchClick = () => {
    if (!canSearch) return
    setAppliedFilters({
      system: draftSystem,
      classId: draftClassId,
      sectionId: draftSectionId,
      groupId: draftGroupId,
    })
    setSelectedStudents([])
    setHasSearched(true)
  }

  const handleResetClick = () => {
    setDraftSystem(undefined)
    setDraftClassId("")
    setDraftSectionId("")
    setDraftGroupId("")
    setGroups([])
    setClasses([])
    setClassOptions([])
    setAppliedFilters({ system: undefined, classId: "", sectionId: "", groupId: "" })
    setStudents([])
    setSelectedStudents([])
    setSearchText("")
    setQuickSearchValue("")
    setHasSearched(false)
  }

  // Bypasses the System/Class requirement entirely — searches by GR number
  // (or name) across every class/system in the current session at once.
  const handleQuickSearch = () => {
    const value = quickSearchValue.trim()
    if (!value) return
    setDraftSystem(undefined)
    setDraftClassId("")
    setDraftSectionId("")
    setDraftGroupId("")
    setAppliedFilters({ system: undefined, classId: "", sectionId: "", groupId: "" })
    setSelectedStudents([])
    setSearchText(value)
    setHasSearched(true)
  }

  const handlePrintSticker = async () => {
    if (!selectedStudents.length) return
    await openFeeStickers(selectedStudents, currentSession?._id)
  }

  const columns = [
    {
      title: "GR Number",
      dataIndex: "grNumber",
      render: (_: unknown, record: Enrollment) => record?.student?.grNumber,
      sorter: (a: Enrollment, b: Enrollment) =>
        (a.student?.grNumber || "").localeCompare(b.student?.grNumber || ""),
    },
    {
      title: "Name",
      dataIndex: "user",
      render: (_: unknown, record: Enrollment) => (
        <div className="d-flex align-items-center">
          <span className="avatar avatar-md">
            <ImageWithBasePath
              src={"assets/img/students/student-01.jpg"}
              fullSrc={record?.user?.profilePicture}
              className="img-fluid rounded-circle"
              alt="img"
            />
          </span>
          <div className="ms-2">
            <p className="text-dark mb-0">
              {`${record?.user?.firstName || ""} ${record?.user?.lastName || ""}`.trim()}
            </p>
          </div>
        </div>
      ),
      sorter: (a: Enrollment, b: Enrollment) =>
        (a.user?.firstName || "").localeCompare(b.user?.firstName || ""),
    },
    {
      title: "Class",
      dataIndex: ["class", "name"],
      sorter: (a: Enrollment, b: Enrollment) => (a.class?.name || "").localeCompare(b.class?.name || ""),
    },
    {
      title: "Section",
      dataIndex: ["section", "name"],
      sorter: (a: Enrollment, b: Enrollment) =>
        (a.section?.name || "").localeCompare(b.section?.name || ""),
    },
    {
      title: "Group",
      dataIndex: ["group", "name"],
      render: (_: unknown, record: Enrollment) => record.group?.name || "N/A",
    },
  ]

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
          <div className="my-auto mb-2">
            <h3 className="page-title mb-1">Fee Sticker Printing</h3>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>Dashboard</Link>
                </li>
                <li className="breadcrumb-item">Academic Year</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Fee Sticker Printing
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <Link to={routes.feeStickerDesigner} className="btn btn-outline-light bg-white">
              <i className="ti ti-edit me-1" />
              Edit Template
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header pb-3 d-flex align-items-start justify-content-between flex-wrap gap-2">
            <div>
              <h4 className="mb-1">Select Students</h4>
              <p className="text-muted small mb-3">
                Choose System and Class (Section and Group are optional), then click{" "}
                <strong>Search</strong> to load matching students. No records show until you search.
              </p>
            </div>

            <div className="d-flex align-items-end flex-wrap w-100">
              <div className="mb-3 me-2">
                <label className="form-label fw-semibold">System</label>
                <CommonSelect
                  className="select"
                  options={systems}
                  value={systems.find((item: any) => item._id === draftSystem)}
                  getOptionValue={(item) => item._id}
                  getOptionLabel={(item) => item.name}
                  onChange={handleSystemChange}
                  placeholder="Select System"
                />
              </div>

              <div className="mb-3 me-2">
                <label className="form-label fw-semibold">Class</label>
                <CommonSelect
                  className="select"
                  options={classOptions}
                  value={classOptions.find((opt) => opt.value === draftClassId)}
                  onChange={handleClassFilterChange}
                  placeholder={
                    loadingClasses
                      ? "Loading..."
                      : !draftSystem
                        ? "Select System first"
                        : "Select Class"
                  }
                  isDisabled={!draftSystem || loadingClasses}
                />
              </div>

              <div className="mb-3 me-2">
                <label className="form-label fw-semibold">
                  Section <span className="text-muted fw-normal">(optional)</span>
                </label>
                <CommonSelect
                  className="select"
                  options={sectionOptions}
                  value={sectionOptions.find((opt) => opt.value === draftSectionId)}
                  onChange={handleSectionFilterChange}
                  placeholder={
                    !draftClassId
                      ? "Select Class first"
                      : sectionOptions.length === 0
                        ? "No sections"
                        : "All sections"
                  }
                  isDisabled={!draftClassId || sectionOptions.length === 0}
                />
              </div>

              {groupOptions.length > 0 && (
                <div className="mb-3 me-2">
                  <label className="form-label fw-semibold">
                    Group <span className="text-muted fw-normal">(optional)</span>
                  </label>
                  <CommonSelect
                    className="select"
                    options={groupOptions}
                    value={groupOptions.find((opt) => opt.value === draftGroupId)}
                    onChange={handleGroupFilterChange}
                    placeholder={loadingGroups ? "Loading..." : "All groups"}
                    isDisabled={loadingGroups}
                  />
                </div>
              )}

              <div className="mb-3 d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSearchClick}
                  disabled={!canSearch}
                  title={canSearch ? "Search students" : "Select System and Class first"}
                >
                  <i className="ti ti-search me-1" />
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleResetClick}
                  title="Reset filters"
                >
                  <i className="ti ti-refresh" />
                </button>
              </div>

              <div className="mb-3 ms-3 ps-3 border-start">
                <label className="form-label fw-semibold">
                  Quick Search <span className="text-muted fw-normal">(GR number or name)</span>
                </label>
                <div className="d-flex gap-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. 634032"
                    value={quickSearchValue}
                    onChange={(e) => setQuickSearchValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleQuickSearch()
                    }}
                    style={{ minWidth: 180 }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleQuickSearch}
                    disabled={!quickSearchValue.trim()}
                    title="Search any single student directly, no System/Class needed"
                  >
                    <i className="ti ti-search" />
                  </button>
                </div>
              </div>
            </div>

            {!canSearch && !hasSearched && (
              <div className="small text-muted mt-2">
                <i className="ti ti-info-circle me-1" />
                Select System and Class to search — Section and Group are optional. Or use Quick Search to jump straight to one student.
              </div>
            )}

            {hasSearched && (
              <div className="d-flex justify-content-between align-items-center mt-3 w-100">
                <span className="text-muted small">
                  {loading
                    ? "Loading…"
                    : `${students.length} student${students.length === 1 ? "" : "s"} found${
                        selectedStudents.length ? `, ${selectedStudents.length} selected` : ""
                      }`}
                </span>
                <button
                  type="button"
                  className="btn btn-dark d-flex align-items-center"
                  disabled={!selectedStudents.length || feeStickerLoading}
                  onClick={handlePrintSticker}
                >
                  <i className="ti ti-ticket me-2" />
                  {feeStickerLoading
                    ? "Loading Stickers..."
                    : `Print Sticker${selectedStudents.length ? ` (${selectedStudents.length})` : ""}`}
                </button>
              </div>
            )}
          </div>

          <div className="card-body p-0 py-3">
            {hasSearched ? (
              <Table
                dataSource={students}
                columns={columns}
                loading={loading}
                setSearchText={setSearchText}
                Selection={true}
                onSelectionChange={(_keys: any[], rows: Enrollment[]) => {
                  setSelectedStudents(rows || [])
                }}
                hidePagination
              />
            ) : (
              <div className="text-center py-5 text-muted">
                <i className="ti ti-list-search" style={{ fontSize: 36 }} />
                <p className="mt-2 mb-0">
                  Select System and Class above, then click <strong>Search</strong> to view students —
                  or use <strong>Quick Search</strong> to find one student by GR number directly.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {feeStickerModal}
    </div>
  )
}

export default FeeStickerPrint
